/**
 * useSemanticSessions.js — Session history management for Semantic Query workspace.
 *
 * Manages:
 *  - Current session_id tracking (sent with every query)
 *  - Session list fetching and caching
 *  - Loading a historical session's messages
 *  - Deleting sessions
 *  - Starting new sessions (clearing current session_id)
 */

import { useState, useCallback, useRef } from 'react'
import { api } from '../api'

export function useSemanticSessions() {
  // Current active session — null means "next query creates a new session"
  const [sessionId, setSessionId] = useState(null)

  // Session list for sidebar
  const [sessions, setSessions] = useState([])
  const [sessionsLoading, setSessionsLoading] = useState(false)

  // Loaded session messages (when restoring history)
  const [loadedMessages, setLoadedMessages] = useState(null)
  const [messagesLoading, setMessagesLoading] = useState(false)

  // Prevent duplicate fetches
  const fetchRef = useRef(0)

  /**
   * Fetch the user's session list for the sidebar.
   *
   * The backend scopes the list to the authenticated identity, so there is no
   * local email lookup and no early return when the email is unknown (Req 4.1, 4.2).
   */
  const fetchSessions = useCallback(async () => {
    const token = ++fetchRef.current
    setSessionsLoading(true)
    try {
      const data = await api.listSessions(50)
      if (token === fetchRef.current) {
        setSessions(Array.isArray(data) ? data : [])
      }
    } catch (err) {
      console.warn('[useSemanticSessions] fetchSessions error:', err.message)
      if (token === fetchRef.current) setSessions([])
    } finally {
      if (token === fetchRef.current) setSessionsLoading(false)
    }
  }, [])

  /** Load a specific session's messages (for history restore) */
  const loadSession = useCallback(async (targetSessionId) => {
    if (!targetSessionId) return null
    setMessagesLoading(true)
    try {
      const data = await api.getSession(targetSessionId, 100)
      setSessionId(targetSessionId)
      setLoadedMessages(data?.messages || [])
      return data
    } catch (err) {
      console.warn('[useSemanticSessions] loadSession error:', err.message)
      return null
    } finally {
      setMessagesLoading(false)
    }
  }, [])

  /** Delete a session */
  const deleteSession = useCallback(async (targetSessionId) => {
    try {
      await api.deleteSession(targetSessionId, false)
      setSessions((prev) => prev.filter((s) => s.session_id !== targetSessionId))
      // If we're deleting the current session, clear it
      if (targetSessionId === sessionId) {
        setSessionId(null)
        setLoadedMessages(null)
      }
      return true
    } catch (err) {
      console.warn('[useSemanticSessions] deleteSession error:', err.message)
      return false
    }
  }, [sessionId])

  /** Start a new session (clears current session_id) */
  const startNewSession = useCallback(() => {
    setSessionId(null)
    setLoadedMessages(null)
  }, [])

  /** Update session_id from a query response */
  const updateSessionFromResponse = useCallback((response) => {
    if (response?.session_id && response.session_id !== sessionId) {
      setSessionId(response.session_id)
    }
  }, [sessionId])

  /** Submit feedback for a message */
  const submitFeedback = useCallback(async (messageId, feedback, comment = '') => {
    if (!sessionId || !messageId) return false
    try {
      await api.submitFeedback(sessionId, messageId, feedback, comment)
      return true
    } catch (err) {
      console.warn('[useSemanticSessions] submitFeedback error:', err.message)
      return false
    }
  }, [sessionId])

  return {
    sessionId,
    setSessionId,
    sessions,
    sessionsLoading,
    fetchSessions,
    loadSession,
    loadedMessages,
    setLoadedMessages,
    messagesLoading,
    deleteSession,
    startNewSession,
    updateSessionFromResponse,
    submitFeedback,
  }
}
